/**
 * Safe DOM helpers for AMP monitoring dashboards.
 * AMP-sourced names and status strings are untrusted even for authenticated users.
 */

/**
 * @param {unknown} value
 * @param {string} [fallback]
 * @returns {string}
 */
export function untrustedText(value, fallback = '') {
    if (value === null || value === undefined) {
        return fallback;
    }

    if (typeof value === 'object') {
        return fallback;
    }

    const text = String(value);

    return text.length > 0 ? text : fallback;
}

/**
 * @param {unknown} instanceId
 * @returns {string|null}
 */
export function instanceMonitorPath(instanceId) {
    const id = untrustedText(instanceId, '');

    if (!/^[A-Za-z0-9._-]{1,128}$/.test(id)) {
        return null;
    }

    return `/amp/monitoring/instance/${encodeURIComponent(id)}`;
}

/**
 * @param {Document} doc
 * @param {string} tag
 * @param {{ className?: string, text?: unknown, attrs?: Record<string, string> }} [options]
 * @returns {HTMLElement}
 */
export function createEl(doc, tag, options = {}) {
    const el = doc.createElement(tag);

    if (options.className) {
        el.className = options.className;
    }

    if (Object.prototype.hasOwnProperty.call(options, 'text')) {
        el.textContent = untrustedText(options.text, '');
    }

    if (options.attrs) {
        Object.entries(options.attrs).forEach(([name, value]) => {
            if (value === null || value === undefined) {
                return;
            }

            el.setAttribute(name, untrustedText(value, ''));
        });
    }

    return el;
}

/**
 * @param {{ running?: unknown, appState?: unknown }} [status]
 * @returns {{ statusClass: string, statusIcon: string }}
 */
export function statusPresentation(status) {
    let statusClass = 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    let statusIcon = 'bg-gray-500';

    if (!status || !status.running) {
        statusClass = 'bg-red-100 text-red-800 dark:bg-red-700 dark:text-red-300';
        statusIcon = 'bg-red-500';
    } else if (status.running && (status.appState === 0 || status.appState === 70)) {
        statusClass = 'bg-yellow-100 text-yellow-800 dark:bg-yellow-700 dark:text-yellow-300';
        statusIcon = 'bg-yellow-500';
    } else if (status.running && status.appState === 20) {
        statusClass = 'bg-green-100 text-green-800 dark:bg-green-700 dark:text-green-300';
        statusIcon = 'bg-green-500';
    } else if (status.running && [10, 40, 50, 30].includes(status.appState)) {
        statusClass = 'bg-blue-100 text-blue-800 dark:bg-blue-700 dark:text-blue-300';
        statusIcon = 'bg-blue-500';
    } else if (status.running && status.appState === 120) {
        statusClass = 'bg-red-100 text-red-800 dark:bg-red-700 dark:text-red-300';
        statusIcon = 'bg-red-500';
    }

    return { statusClass, statusIcon };
}

/**
 * @param {ParentNode} parent
 * @param {...(Node|null|undefined)} nodes
 */
function append(parent, ...nodes) {
    nodes.forEach((node) => {
        if (node) {
            parent.appendChild(node);
        }
    });
}

/**
 * @param {HTMLElement} container
 * @param {unknown} data
 * @param {Document} [doc]
 */
export function renderStatusTables(container, data, doc = (typeof document !== 'undefined' ? document : undefined)) {
    if (!container || !doc) {
        return;
    }

    const payload = data && typeof data === 'object' ? data : {};
    const targets = Array.isArray(payload.targets)
        ? payload.targets
        : Array.isArray(payload.statusInfo)
            ? payload.statusInfo
            : [];

    container.replaceChildren();

    targets.forEach((target) => {
        const card = createEl(doc, 'div', {
            className: 'bg-white dark:bg-gray-800 overflow-hidden shadow-xl sm:rounded-lg mb-6',
        });
        const body = createEl(doc, 'div', { className: 'p-6' });
        const heading = createEl(doc, 'h3', {
            className: 'text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4',
            text: untrustedText(target?.friendly_name || target?.name, 'Unknown Target'),
        });
        const tableWrap = createEl(doc, 'div', { className: 'overflow-x-auto' });
        const table = createEl(doc, 'table', { className: 'min-w-full bg-white dark:bg-gray-800' });
        const thead = createEl(doc, 'thead');
        const headerRow = createEl(doc, 'tr');

        [
            { text: 'Instance', className: 'py-2 px-4 border-b border-gray-200 dark:border-gray-700 text-left' },
            { text: 'Status', className: 'py-2 px-4 border-b border-gray-200 dark:border-gray-700 text-center' },
            { text: 'Action', className: 'py-2 px-4 border-b border-gray-200 dark:border-gray-700 text-center' },
        ].forEach((header) => {
            append(headerRow, createEl(doc, 'th', header));
        });

        append(thead, headerRow);

        const tbody = createEl(doc, 'tbody');
        const instances = Array.isArray(target?.instances) ? target.instances : [];

        if (instances.length === 0) {
            const emptyRow = createEl(doc, 'tr');
            const emptyCell = createEl(doc, 'td', {
                className: 'py-3 px-6 text-center text-gray-500 dark:text-gray-400',
                text: 'No instances found',
                attrs: { colspan: '3' },
            });
            append(emptyRow, emptyCell);
            append(tbody, emptyRow);
        } else {
            instances.forEach((instance) => {
                const { statusClass, statusIcon } = statusPresentation(instance?.status);
                const row = createEl(doc, 'tr');

                const nameCell = createEl(doc, 'td', { className: 'py-3 px-6 text-left' });
                nameCell.textContent = untrustedText(instance?.friendly_name || instance?.name, 'Unknown');

                const statusCell = createEl(doc, 'td', { className: 'py-3 px-6 text-center' });
                const statusWrap = createEl(doc, 'div', { className: 'flex items-center justify-center' });
                const icon = createEl(doc, 'span', {
                    className: `inline-block w-3 h-3 ${statusIcon} rounded-full mr-2`,
                });
                const badge = createEl(doc, 'span', {
                    className: `py-1 px-3 rounded-full text-xs ${statusClass}`,
                    text: untrustedText(instance?.status?.combinedStatus, 'Unknown'),
                });
                append(statusWrap, icon, badge);
                append(statusCell, statusWrap);

                const actionCell = createEl(doc, 'td', { className: 'py-3 px-6 text-center' });
                const href = instanceMonitorPath(instance?.id);

                if (href) {
                    const link = createEl(doc, 'a', {
                        className: 'bg-blue-600 hover:bg-blue-700 text-white py-1 px-3 rounded text-sm transition-colors',
                        text: 'Monitor',
                    });
                    link.setAttribute('href', href);
                    append(actionCell, link);
                } else {
                    actionCell.textContent = 'Monitor';
                }

                append(row, nameCell, statusCell, actionCell);
                append(tbody, row);
            });
        }

        append(table, thead, tbody);
        append(tableWrap, table);
        append(body, heading, tableWrap);
        append(card, body);
        append(container, card);
    });
}

/**
 * @param {HTMLElement} container
 * @param {string} heading
 * @param {unknown} payload
 * @param {Document} [doc]
 */
export function renderJsonDebugBlock(container, heading, payload, doc = (typeof document !== 'undefined' ? document : undefined)) {
    if (!container || !doc) {
        return;
    }

    container.replaceChildren();

    const title = createEl(doc, 'h3', {
        className: 'text-lg font-bold mb-2',
        text: heading,
    });
    const pre = createEl(doc, 'pre');
    pre.textContent = typeof payload === 'string' ? payload : JSON.stringify(payload, null, 2);

    append(container, title, pre);
}

/**
 * @param {HTMLElement} statusElement
 * @param {{ status: unknown, lastFetch?: string|null, isPolling: boolean, pollingInterval: number }} details
 * @param {Document} [doc]
 */
export function renderApiStatusDetails(
    statusElement,
    details,
    doc = (typeof document !== 'undefined' ? document : undefined),
) {
    if (!statusElement || !doc) {
        return;
    }

    statusElement.replaceChildren();

    const rows = [
        ['Status:', untrustedText(details.status, 'Unknown')],
        ['Last Fetch:', untrustedText(details.lastFetch, 'N/A')],
        ['Polling:', details.isPolling ? 'Active' : 'Paused'],
        ['Interval:', `${untrustedText(details.pollingInterval, '0')}ms`],
    ];

    rows.forEach(([label, value]) => {
        const row = createEl(doc, 'div');
        const strong = createEl(doc, 'strong', { text: label });
        row.appendChild(strong);
        row.appendChild(doc.createTextNode(` ${value}`));
        statusElement.appendChild(row);
    });
}
